<template>
  <div>
    <el-form
      class="columns is-multiline"
      ref="form"
      :model="form"
      :rules="rules"
      labelPosition="top"
      label-width="80px"
      :inline="true"
      :hide-required-asterisk="true"
    >
      <div class="column">
        <el-card v-loading="initLoading">
          <el-form-item>
            <label class="label" slot="label">{{ $t('project.pick') }}</label>
            <el-select
              v-model="form.project"
              filterable
              :placeholder="$t('project.pick')"
              @change="onProjectChange"
            >
              <el-option
                v-for="project in projectList"
                :key="project._id"
                :value="project._id"
                :label="project.project_id"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item required="required" prop="instrumentSelection">
            <label class="label" slot="label">{{ $t('chart.instrument.pick') }}</label>
            <el-cascader
              v-model="form.instrumentSelection"
              style="min-width:220px"
              :options="projectOptions"
              :props="cascaderProps"
              :show-all-levels="false"
              :placeholder="$t('chart.instrument.pick')"
              @change="onFilterChange"
            ></el-cascader>
          </el-form-item>
        </el-card>
      </div>
      <div class="column">
        <el-card>
          <el-form-item required="required" prop="dateRange">
            <div class="flex-row align-center justify-between" style="height:36px">
              <label class="label" slot="label">瀏覽範圍（尋找缺值用）</label>
              <div>
                <el-button
                  v-for="range in quickRangeList"
                  :key="range.label"
                  size="mini"
                  type="primary"
                  :class="{ 'is-plain': quickRangeActive !== range.label }"
                  @click="setQuickRange(range)"
                >{{ range.label }}</el-button>
              </div>
            </div>
            <el-date-picker
              v-model="form.dateRange"
              type="datetimerange"
              :start-placeholder="$t('chart.date.start')"
              :end-placeholder="$t('chart.date.end')"
              :default-time="['00:00:00']"
              format="yyyy/MM/dd HH:mm"
              @change="onFilterChange"
            ></el-date-picker>
          </el-form-item>
        </el-card>
      </div>
    </el-form>

    <el-card v-if="hasFetched" v-loading="fetchLoading" class="mb-16">
      <div slot="header">原始資料（用來尋找缺值範圍）</div>
      <div class="gapfill-chart">
        <highcharts :options="chart1Options"></highcharts>
      </div>
    </el-card>

    <el-card v-if="hasFetched" class="mb-16">
      <div slot="header">補值工具參數</div>
      <div class="flex-row">
        <el-form :inline="true" label-position="top">
          <el-form-item>
            <span slot="label">
              資料頻率（分鐘）
              <el-tooltip effect="dark" placement="top" content="儀器理論上多久記錄一筆資料。用來展開「應該存在但缺漏」的時間點網格，設錯會導致誤判缺值或漏判缺值。">
                <font-awesome-icon icon="question-circle" class="hint-icon" />
              </el-tooltip>
            </span>
            <el-input-number v-model="form.intervalMinutes" :min="1" :step="1" @change="markToolParamsStale"></el-input-number>
          </el-form-item>
          <el-form-item>
            <span slot="label">
              offsetDays
              <el-tooltip effect="dark" placement="top" content="計算歷史同鐘點參考時，會跳過缺值時間點往前 N 天內的資料，避免用到還不穩定、尚未確認的最近資料。缺值區間頭尾銜接一律使用最接近的真實資料，不受此設定影響。">
                <font-awesome-icon icon="question-circle" class="hint-icon" />
              </el-tooltip>
            </span>
            <el-input-number v-model="form.offsetDays" :min="0" :step="1" @change="markToolParamsStale"></el-input-number>
          </el-form-item>
          <el-form-item>
            <span slot="label">
              noiseStdScale
              <el-tooltip effect="dark" placement="top" content="疊加在補值上的隨機雜訊幅度（乘在歷史同鐘點偏差的標準差上）。數值越大補值波動越明顯，設為 0 則不加雜訊，補值會是平滑的趨勢線。">
                <font-awesome-icon icon="question-circle" class="hint-icon" />
              </el-tooltip>
            </span>
            <el-input-number v-model="form.noiseStdScale" :min="0" :step="0.1" @change="markToolParamsStale"></el-input-number>
          </el-form-item>
        </el-form>
        <div class="param-suggestion">
          <div class="param-suggestion-title">建議數值</div>
          <div>offsetDays：建議 3（跳過近 3 天內尚未穩定的資料）</div>
          <div>noiseStdScale：建議 0.5（雜訊幅度適中；設 0 為平滑趨勢線）</div>
        </div>
      </div>
      <el-collapse>
        <el-collapse-item title="進階參數">
          <div class="flex-row">
            <el-form :inline="true" label-position="top">
              <el-form-item>
                <span slot="label">
                  maxLookbackDays
                  <el-tooltip effect="dark" placement="top" content="尋找「同一鐘點」歷史資料算季節性偏差時，最多往回找幾天（從 offsetDays 那天開始算）。找不到足夠天數的資料就用目前已收集到的筆數計算。">
                    <font-awesome-icon icon="question-circle" class="hint-icon" />
                  </el-tooltip>
                </span>
                <el-input-number v-model="form.maxLookbackDays" :min="1" :step="1" @change="markToolParamsStale"></el-input-number>
              </el-form-item>
              <el-form-item>
                <span slot="label">
                  averageDaysN
                  <el-tooltip effect="dark" placement="top" content="計算「季節性偏差」平均值時，最多採用幾天的歷史同鐘點資料。找到這麼多筆就停止往回找，不會用到全部 maxLookbackDays 天份的資料。">
                    <font-awesome-icon icon="question-circle" class="hint-icon" />
                  </el-tooltip>
                </span>
                <el-input-number v-model="form.averageDaysN" :min="1" :step="1" @change="markToolParamsStale"></el-input-number>
              </el-form-item>
              <el-form-item>
                <span slot="label">
                  補值目標範圍（留空＝套用瀏覽範圍內全部缺值）
                  <el-tooltip effect="dark" placement="top" content="只針對此範圍內的缺值計算並寫入，其餘缺值不受影響。範圍不可超出上方「瀏覽範圍」，超出會自動裁切。">
                    <font-awesome-icon icon="question-circle" class="hint-icon" />
                  </el-tooltip>
                </span>
                <el-date-picker
                  v-model="form.fillDateRange"
                  type="datetimerange"
                  :start-placeholder="$t('chart.date.start')"
                  :end-placeholder="$t('chart.date.end')"
                  :default-time="['00:00:00']"
                  format="yyyy/MM/dd HH:mm"
                  clearable
                  :picker-options="fillRangePickerOptions"
                  @change="onFillRangeChange"
                ></el-date-picker>
                <div class="hint-text">
                  只針對此範圍內的缺值計算並寫入，範圍不可超出上方「瀏覽範圍」
                </div>
              </el-form-item>
            </el-form>
            <div class="param-suggestion">
              <div class="param-suggestion-title">建議數值</div>
              <div>maxLookbackDays：建議 7（回溯 7 天內的同鐘點歷史資料）</div>
              <div>averageDaysN：建議 3（取 3 天平均計算季節性偏差）</div>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
      <div class="mt-16">
        <el-button type="primary" :loading="computeLoading" @click="computeFill">
          {{ hasComputed ? '重新產生補值' : '產生補值結果' }}
        </el-button>
      </div>
    </el-card>

    <el-alert
      v-if="hasComputed && chart2Stale"
      title="參數或瀏覽範圍已變更，目前顯示的補值結果已過期，請重新產生補值後再送出"
      type="warning"
      show-icon
      :closable="false"
      class="mb-16"
    ></el-alert>

    <el-card v-if="hasComputed" v-loading="computeLoading" class="mb-16">
      <div slot="header">補值後結果</div>
      <el-descriptions :column="4" border class="mb-16">
        <el-descriptions-item label="應補值時間點">{{ plans.length }}</el-descriptions-item>
        <el-descriptions-item label="可計算補值">{{ filledCount }}</el-descriptions-item>
        <el-descriptions-item label="無法計算">{{ blankCount }}</el-descriptions-item>
        <el-descriptions-item label="將寫入筆數">{{ selectedCount }}</el-descriptions-item>
      </el-descriptions>

      <div class="gapfill-chart">
        <highcharts :options="chart2Options"></highcharts>
      </div>

      <div class="flex-row justify-between align-center mt-16">
        <el-button size="small" @click="showDetailTable = !showDetailTable">
          {{ showDetailTable ? '隱藏明細表格' : '顯示明細表格（可編輯個別時間點）' }}
        </el-button>
        <el-button
          type="primary"
          :disabled="chart2Stale || !selectedCount"
          :loading="submitLoading"
          @click="submitPlans"
        >
          確認補值並寫入（{{ selectedCount }} 筆）
        </el-button>
      </div>

      <div v-if="showDetailTable" class="mt-16">
        <hot-table ref="hot" :settings="hotSettings"></hot-table>
      </div>
    </el-card>
  </div>
</template>

<script>
import moment from 'moment';
import MomentTimeZone from 'moment-timezone';
import { HotTable } from '@handsontable/vue';
import { computeGapFillPlans, expandFetchDateRange, DEFAULT_PARAMS } from '@/lib/base/outputGapFill';
import { getInstrumentDisplayName } from '@/lib/base/instrumentCascade';

window.moment = moment;
MomentTimeZone();

const CHART_TIMEZONE = 'Asia/Taipei';
const CHART_BACKGROUND_COLOR = '#F5F7FA';

function hasValidReading(value) {
  if (value === null || value === undefined || value === '') return false;
  return !Number.isNaN(Number(value));
}

export default {
  components: {
    HotTable,
  },
  data() {
    return {
      initLoading: false,
      fetchLoading: false,
      computeLoading: false,
      submitLoading: false,
      projectOptions: [],
      instrumentMap: {},
      cascaderProps: {
        label: 'label',
        value: 'value',
        children: 'children',
      },
      form: {
        project: '',
        instrumentSelection: [],
        dateRange: [
          moment().subtract(8, 'd').startOf('day').toISOString(),
          moment().subtract(1, 'd').startOf('day').toISOString(),
        ],
        intervalMinutes: 60,
        offsetDays: 3,
        noiseStdScale: DEFAULT_PARAMS.noiseStdScale,
        maxLookbackDays: DEFAULT_PARAMS.maxLookbackDays,
        averageDaysN: DEFAULT_PARAMS.averageDaysN,
        fillDateRange: null,
      },
      rules: {
        instrumentSelection: [
          { type: 'array', required: true, message: this.$t('chart.error.instrument'), trigger: 'change' },
        ],
        dateRange: [
          { type: 'array', required: true, message: this.$t('chart.error.date'), trigger: 'change' },
        ],
      },
      quickRangeList: [
        { label: '1D', days: 1 },
        { label: '7D', days: 7 },
        { label: '1M', days: 30 },
      ],
      quickRangeActive: null,
      rawRecords: [],
      referenceRecords: [],
      plans: [],
      hasFetched: false,
      hasComputed: false,
      chart2Stale: false,
      chart1Options: {},
      chart2Options: {},
      showDetailTable: false,
    };
  },
  computed: {
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
    projectList() {
      const allProjects = this.$store.getters['project/projectList'];
      const isAdmin = this.$store.getters['user/isAdmin'];
      if (isAdmin) return allProjects;
      const managedProjects = this.$store.getters['user/managedProjects'];
      return allProjects.filter(p => managedProjects.includes(p._id));
    },
    instrumentId() {
      return this.form.instrumentSelection && this.form.instrumentSelection[2];
    },
    selectedInstrumentName() {
      const instrument = this.instrumentMap[this.instrumentId];
      return instrument ? getInstrumentDisplayName(instrument) : '';
    },
    blankCount() {
      return this.plans.filter(p => p.blank).length;
    },
    filledCount() {
      return this.plans.length - this.blankCount;
    },
    selectedCount() {
      return this.plans.filter(p => p.include && hasValidReading(p.reading2)).length;
    },
    fillRangePickerOptions() {
      const dateRange = this.form.dateRange;
      return {
        disabledDate: time => {
          if (!dateRange || dateRange.length !== 2) return false;
          const startDay = moment(dateRange[0]).startOf('day').valueOf();
          const endDay = moment(dateRange[1]).endOf('day').valueOf();
          return time.getTime() < startDay || time.getTime() > endDay;
        },
      };
    },
    effectiveFillRange() {
      if (this.form.fillDateRange && this.form.fillDateRange.length === 2) {
        return this.form.fillDateRange;
      }
      return this.form.dateRange;
    },
    hotSettings() {
      const vm = this;
      return {
        data: this.plans,
        colHeaders: ['時間', '狀態', '補值 reading2', '寫入'],
        colWidths: [180, 100, 140, 60],
        rowHeaders: true,
        manualRowResize: true,
        manualColumnResize: true,
        height: 400,
        width: '100%',
        undo: true,
        columns: [
          { data: 'timeLabel', readOnly: true },
          {
            data: 'blank',
            readOnly: true,
            renderer(instance, td, row) {
              const rowData = instance.getSourceDataAtRow(row);
              td.innerText = rowData.blank ? '無法計算' : '可計算';
              td.style.color = rowData.blank ? '#F56C6C' : '#67C23A';
              return td;
            },
          },
          { data: 'reading2' },
          { data: 'include', type: 'checkbox' },
        ],
        beforeChange(changes) {
          if (!changes) return;
          changes.forEach(([row, prop, oldValue, newValue]) => {
            if (oldValue === newValue) return;
            const target = vm.plans[row];
            if (!target) return;
            if (prop === 'reading2') {
              const numeric = newValue === '' || newValue === null ? '' : Number(newValue);
              target.reading2 = Number.isNaN(numeric) ? '' : numeric;
              target.blank = !hasValidReading(target.reading2);
              target.include = hasValidReading(target.reading2);
            } else if (prop === 'include') {
              target.include = !!newValue;
            }
          });
          vm.$nextTick(() => vm.buildChart2Options());
        },
        licenseKey: 'non-commercial-and-evaluation',
      };
    },
  },
  mounted() {
    const isValidProject = this.projectList.some(p => p._id === this.selectedProject._id);
    if (isValidProject) {
      this.form.project = this.selectedProject._id;
      this.initCascaderSelector();
    } else {
      this.$notifyError('無可用專案', '您沒有權限訪問當前專案的設定');
      this.$router.push('/');
    }
  },
  methods: {
    onProjectChange() {
      this.form.instrumentSelection = [];
      this.resetResults();
      this.initCascaderSelector();
    },
    initCascaderSelector() {
      this.initLoading = true;
      this.projectOptions = [];
      this.instrumentMap = {};
      return this.$axios({
        category: 'project',
        method: 'get',
        apiName: `${this.form.project}/area-station-instrument`,
      })
        .then(res => {
          this.projectOptions = res.data.area.map(area => ({
            label: area.name,
            disabled: area.station.length === 0,
            children: area.station.map(station => ({
              label: station.name,
              disabled: station.instrument.length === 0,
              children: station.instrument.map(instrument => {
                this.instrumentMap[instrument._id] = instrument;
                return {
                  label: getInstrumentDisplayName(instrument),
                  value: instrument._id,
                };
              }),
            })),
          }));
        })
        .catch(error => {
          this.$notifyError(error);
        })
        .finally(() => {
          this.initLoading = false;
        });
    },
    resetResults() {
      this.hasFetched = false;
      this.hasComputed = false;
      this.chart2Stale = false;
      this.rawRecords = [];
      this.referenceRecords = [];
      this.plans = [];
      this.chart1Options = {};
      this.chart2Options = {};
    },
    setQuickRange(range) {
      this.quickRangeActive = range.label;
      this.form.dateRange = [
        moment().subtract(range.days, 'd').startOf('day').toISOString(),
        moment().toISOString(),
      ];
      this.onFilterChange();
    },
    onFilterChange() {
      this.hasComputed = false;
      this.chart2Stale = false;
      this.plans = [];
      this.chart2Options = {};
      this.form.fillDateRange = null;
      if (this.form.project && this.instrumentId && this.form.dateRange && this.form.dateRange.length === 2) {
        this.fetchRawData();
      }
    },
    onFillRangeChange(value) {
      if (!value || value.length !== 2) {
        this.form.fillDateRange = null;
        this.markToolParamsStale();
        return;
      }

      const [browseStart, browseEnd] = this.form.dateRange;
      const browseStartMs = moment(browseStart).valueOf();
      const browseEndMs = moment(browseEnd).valueOf();
      let startMs = moment(value[0]).valueOf();
      let endMs = moment(value[1]).valueOf();
      let clamped = false;

      if (startMs < browseStartMs) { startMs = browseStartMs; clamped = true; }
      if (endMs > browseEndMs) { endMs = browseEndMs; clamped = true; }
      if (startMs > endMs) { startMs = browseStartMs; endMs = browseEndMs; }

      this.form.fillDateRange = [moment(startMs).toISOString(), moment(endMs).toISOString()];
      if (clamped) this.$notifyInfo('補值目標範圍已自動限制在瀏覽範圍內');
      this.markToolParamsStale();
    },
    markToolParamsStale() {
      if (this.hasComputed) this.chart2Stale = true;
    },
    async fetchRawData() {
      const instrumentId = this.instrumentId;
      if (!instrumentId) return;

      this.fetchLoading = true;
      try {
        const [startDate, endDate] = this.form.dateRange;
        const res = await this.$axios({
          category: 'project',
          method: 'post',
          apiName: `${this.form.project}/output?type=all`,
          data: {
            instrument: [instrumentId],
            date: [startDate, endDate],
            timezone: 'Asia/Taipei',
          },
        });

        this.rawRecords = res.data;
        this.hasFetched = true;
        this.buildChart1Options();

        if (this.rawRecords.length > 20000) {
          this.$notifyInfo('資料量較大', '目前瀏覽範圍內原始資料點數較多，建議縮小範圍以提升效能');
        }
      } catch (error) {
        this.$notifyError(error);
      } finally {
        this.fetchLoading = false;
      }
    },
    buildChart1Options() {
      const data = this.rawRecords
        .filter(r => hasValidReading(r.reading2))
        .map(r => [moment(r.TIMESTAMP).valueOf(), Number(r.reading2)])
        .sort((a, b) => a[0] - b[0]);

      this.chart1Options = {
        time: { timezone: CHART_TIMEZONE },
        chart: { zoomType: 'x', backgroundColor: CHART_BACKGROUND_COLOR },
        title: { text: null },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Output' } },
        tooltip: { xDateFormat: '%Y-%m-%d %H:%M' },
        series: [
          {
            name: '原始資料',
            data,
            color: '#409EFF',
            marker: { enabled: true, radius: 2, fillColor: '#409EFF', lineWidth: 0 },
          },
        ],
      };
    },
    async computeFill() {
      const valid = await new Promise(resolve => this.$refs.form.validate(resolve));
      if (!valid) return;

      const instrumentId = this.instrumentId;
      if (!instrumentId || !this.hasFetched) {
        this.$notifyError('請先選擇儀器並抓取資料');
        return;
      }

      if (this.hasComputed) {
        const response = await this.$confirmEdit('重新產生補值將覆蓋目前明細表格中的手動修改');
        if (response !== 'confirm') return;
      }

      this.computeLoading = true;
      try {
        const [startDate, endDate] = this.effectiveFillRange;
        const [fetchStart, fetchEnd] = expandFetchDateRange({
          startDate,
          endDate,
          offsetDays: this.form.offsetDays,
          maxLookbackDays: this.form.maxLookbackDays,
        });

        const res = await this.$axios({
          category: 'project',
          method: 'post',
          apiName: `${this.form.project}/output?type=all`,
          data: {
            instrument: [instrumentId],
            date: [fetchStart, fetchEnd],
            timezone: 'Asia/Taipei',
          },
        });

        this.referenceRecords = res.data;
        const instrument = this.instrumentMap[instrumentId];
        const decimalPoints = (instrument && instrument.latestVersion && instrument.latestVersion.decimal_points) ?? 2;

        const { plans } = computeGapFillPlans(this.referenceRecords, {
          startDate,
          endDate,
          intervalMinutes: this.form.intervalMinutes,
          offsetDays: this.form.offsetDays,
          maxLookbackDays: this.form.maxLookbackDays,
          averageDaysN: this.form.averageDaysN,
          noiseStdScale: this.form.noiseStdScale,
          decimalPoints,
        });

        this.plans = plans.map(p => ({
          ...p,
          timeLabel: moment.tz(p.TIMESTAMP, 'Asia/Taipei').format('YYYY/MM/DD HH:mm'),
          include: !p.blank,
        }));

        this.hasComputed = true;
        this.chart2Stale = false;
        this.buildChart2Options();
        this.$nextTick(() => {
          if (this.$refs.hot && this.$refs.hot.hotInstance) {
            this.$refs.hot.hotInstance.loadData(this.plans);
          }
        });

        if (!plans.length) {
          this.$notifyInfo('該時段內沒有缺值');
        } else if (this.blankCount) {
          this.$notifyInfo('部分時間點無法計算', `共 ${this.blankCount} 筆缺值缺乏足夠參考資料，可於明細表格手動輸入`);
        }
      } catch (error) {
        this.$notifyError(error);
      } finally {
        this.computeLoading = false;
      }
    },
    buildChart2Options() {
      const [startDate, endDate] = this.form.dateRange;
      const startMs = moment(startDate).valueOf();
      const endMs = moment(endDate).valueOf();

      const realPoints = this.referenceRecords
        .filter(r => {
          const t = moment(r.TIMESTAMP).valueOf();
          if (t < startMs || t > endMs) return false;
          return hasValidReading(r.reading2);
        })
        .map(r => [moment(r.TIMESTAMP).valueOf(), Number(r.reading2)])
        .sort((a, b) => a[0] - b[0]);

      const filledPoints = this.plans
        .filter(p => p.include && hasValidReading(p.reading2))
        .map(p => [p.TIMESTAMP, Number(p.reading2)])
        .sort((a, b) => a[0] - b[0]);

      const skippedTimestamps = this.plans
        .filter(p => p.blank || !p.include || !hasValidReading(p.reading2))
        .map(p => p.TIMESTAMP);

      this.chart2Options = {
        time: { timezone: CHART_TIMEZONE },
        chart: { zoomType: 'x', backgroundColor: CHART_BACKGROUND_COLOR },
        title: { text: null },
        xAxis: {
          type: 'datetime',
          plotLines: skippedTimestamps.map(t => ({
            value: t,
            color: '#F56C6C',
            dashStyle: 'Dot',
            width: 1,
            label: skippedTimestamps.length <= 20 ? { text: '未寫入', rotation: 90, style: { color: '#F56C6C' } } : undefined,
          })),
        },
        yAxis: { title: { text: 'Output' } },
        tooltip: { xDateFormat: '%Y-%m-%d %H:%M' },
        series: [
          {
            name: '原始資料',
            data: realPoints,
            color: '#409EFF',
            marker: { enabled: true, radius: 2, fillColor: '#409EFF', lineWidth: 0 },
          },
          {
            name: '補值',
            data: filledPoints,
            color: '#E6A23C',
            marker: { enabled: true, radius: 2, fillColor: '#E6A23C', lineWidth: 0 },
          },
        ],
      };
    },
    async submitPlans() {
      if (this.chart2Stale) return;
      const instrumentId = this.instrumentId;
      const updates = this.plans
        .filter(p => p.include && hasValidReading(p.reading2))
        .map(p => ({ TIMESTAMP: new Date(p.TIMESTAMP).toISOString(), reading2: p.reading2 }));

      if (!updates.length) {
        this.$notifyInfo('沒有可寫入的補值資料');
        return;
      }

      const response = await this.$confirmEdit(
        `即將寫入 ${updates.length} 筆補值資料至「${this.selectedInstrumentName}」`,
      );
      if (response !== 'confirm') return;

      this.submitLoading = true;
      const chunkSize = 5000;
      const chunks = this.$lodash.chunk(updates, chunkSize);
      let writtenCount = 0;
      let invalidTimestampCount = 0;
      try {
        for (const chunk of chunks) {
          // eslint-disable-next-line no-await-in-loop
          const res = await this.$axios({
            category: 'admin',
            method: 'post',
            apiName: `instrument/${instrumentId}/output/bulk`,
            data: { updates: chunk },
          });
          writtenCount += res.data.writtenCount || 0;
          invalidTimestampCount += res.data.invalidTimestampCount || 0;
        }
        this.$notifySuccess(
          '補值寫入完成',
          `成功寫入 ${writtenCount} 筆${invalidTimestampCount ? `，${invalidTimestampCount} 筆時間格式錯誤被略過` : ''}`,
        );
        this.resetResults();
      } catch (error) {
        this.$notifyError(error);
      } finally {
        this.submitLoading = false;
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.mb-16
  margin-bottom 16px
.mt-16
  margin-top 16px
.hint-text
  font-size 12px
  color #909399
  margin-top 4px
.hint-icon
  color #909399
  margin-left 4px
  cursor help
.param-suggestion
  margin-left 24px
  padding 8px 12px
  background #F5F7FA
  border-radius 4px
  font-size 12px
  color #606266
  line-height 1.8
  white-space nowrap
.param-suggestion-title
  font-weight bold
  color #303133
  margin-bottom 2px
// chart/chart.vue 全域 import 了 highcharts/css/highcharts.css（styled mode 樣式表），
// 對 SVG 而言外部樣式表一律蓋過 fill/stroke 這類 presentation attribute，
// 所以這裡的顏色/背景設定必須用 !important 蓋回來，光靠 options 裡的 color 沒有用。
.gapfill-chart
  ::v-deep .highcharts-background
    fill #F5F7FA !important
  ::v-deep .highcharts-markers
    stroke none !important
  ::v-deep .highcharts-point
    stroke none !important
  ::v-deep .highcharts-color-0
    fill #409EFF !important
    stroke #409EFF !important
  ::v-deep .highcharts-color-1
    fill #E6A23C !important
    stroke #E6A23C !important
</style>
